import {
  type CSSProperties,
  forwardRef,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';

import classNames from 'classnames';

import type { HDirection, VDirection } from './utils';

import styles from './popover.module.css';

type Props = {
  opened?: boolean;
  top?: number;
  left?: number;
  right?: number;
  bottom?: number;
  hDirection: HDirection;
  vDirection: VDirection;
  portal?: HTMLElement;
  dark?: boolean;
  usePortal?: boolean;
  isFullWidth?: boolean;
  className?: string;

  /**
   * If `true` the out animation won't happen.
   */
  closeImmediately?: boolean;

  onMeasuredOpen?: (rect: DOMRect) => void;

  animation?: 'scale' | 'expand_vertical';
};

const Popover = forwardRef<HTMLDivElement, PropsWithChildren<Props>>(
  (
    {
      usePortal = true,
      portal,
      opened,
      left,
      right,
      top,
      bottom,
      vDirection,
      hDirection,
      children,
      closeImmediately = true,
      animation = 'scale',
      onMeasuredOpen,
      className,
      isFullWidth,
    },
    ref,
  ) => {
    const innerRef = useRef<HTMLDivElement>(null);
    const [innerShow, setInnerShow] = useState(false);
    const [measuring, setMeasuring] = useState(false);

    const openHandler = useCallback(() => {
      if (!innerRef.current) {
        return;
      }
      innerRef.current.style.pointerEvents = 'auto';
      innerRef.current.removeEventListener('animationend', openHandler);
    }, []);

    const closeHandler = useCallback(() => {
      if (!innerRef.current) {
        return;
      }
      setInnerShow(false);
      innerRef.current.style.pointerEvents = 'none';
      innerRef.current.removeEventListener('animationend', closeHandler);
    }, []);

    useEffect(() => {
      if (opened) {
        setInnerShow(true);
        // Measure routine
        setMeasuring(true);
      }
      if (!opened) {
        if (closeImmediately) {
          closeHandler();
        } else {
          if (!innerRef.current) {
            return;
          }
          innerRef.current.addEventListener('animationend', closeHandler);
        }
      }
    }, [opened, closeHandler, closeImmediately]);

    useEffect(() => {
      if (!measuring) {
        return;
      }
      if (!innerRef.current) {
        return;
      }
      const rect = innerRef.current.getBoundingClientRect();
      onMeasuredOpen?.(rect);
      setMeasuring(false);
      innerRef.current.addEventListener('animationend', openHandler);
    }, [measuring, onMeasuredOpen, openHandler]);

    if (!opened && !innerShow) {
      return null;
    }

    const style: CSSProperties = {};
    if (left !== undefined) {
      style.left = `${left}px`;
    }
    if (top !== undefined) {
      style.top = `${top}px`;
    }
    if (right !== undefined) {
      style.right = `${right}px`;
    }
    if (bottom !== undefined) {
      style.bottom = `${bottom}px`;
    }

    const popover = (
      <div
        className={classNames(styles.root, className)}
        role="dialog"
        style={style}
        ref={ref}
      >
        <div
          className={classNames(
            styles.content,
            styles[vDirection],
            styles[hDirection],
            {
              [styles.measuring]: measuring,
              [styles.in]: opened,
              [styles.out]: !opened && !closeImmediately,
              [styles.scale]: animation === 'scale',
              [styles.expand_vertical]: animation === 'expand_vertical',
              [styles.full_width]: isFullWidth,
            },
          )}
          ref={innerRef}
          style={{
            pointerEvents: 'none',
          }}
        >
          {children}
        </div>
      </div>
    );
    const fullscreenElement =
      document.fullscreenElement === window.document.documentElement
        ? document.body
        : document.fullscreenElement;
    return usePortal
      ? ReactDOM.createPortal(
          popover,
          portal ?? fullscreenElement ?? document.body,
        )
      : popover;
  },
);

export default Popover;
